import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

export const useAppStore = defineStore('app', () => {
  const router = useRouter()
  const allowUploads = ref(false)
  const route = useRoute()
  const isNotAuthorisedPage = computed(() => route.name === 'NotAuthorised')
  // TODO: setting this to true for now to avoid redirecting to NotAuthorised page
  const isUserAuthorisedForEverGpt = ref(true)

  const isAppLoaded = ref(false)
  /**
   * While false, `beforeEach` skips most guards so Keycloak can finish first.
   * The initial URL is still resolved once without auth — so after bootstrap we
   * run a forced `replace` of the same location so guards (e.g. auth) actually run
   * before `isAppLoaded` reveals `RouterView`.
   */
  const isBootstrapRouteRevalidation = ref(false)

  const shouldRunRouterGuards = computed(
    () => isAppLoaded.value || isBootstrapRouteRevalidation.value,
  )

  /**
   * Same-URL navigation is normally a no-op; `force` makes `beforeEach` run again.
   *
   * Resolves the target from `window.location`, NOT `router.currentRoute`: until the
   * initial navigation settles, `currentRoute` is still the router's placeholder `/`,
   * so a bootstrap that finishes first (fast API + warm route chunks) used to replace
   * a deep link — `/gpt`, `/gpt/:id`, `/library` — with the home page.
   */
  const replaceCurrentLocationForGuardRerun = () => {
    const { pathname, search, hash } = window.location
    const target = router.resolve(`${pathname}${search}${hash}`)
    return router.replace({
      path: target.path,
      query: target.query,
      hash: target.hash,
      force: true,
    })
  }

  const setIsUserAuthorisedForEverGpt = (value: boolean) => {
    isUserAuthorisedForEverGpt.value = value

    /**
     * Only worth re-running the guards when they actually run. During bootstrap they
     * are skipped, so this `replace` decided nothing — it only raced the one
     * `setIsAppLoaded` fires moments later against the SAME location. vue-router
     * cancels one of two concurrent navigations, and the loser can be the bootstrap
     * pass that restores a shared deep link, which then never happens. The bootstrap
     * pass evaluates this flag anyway, so skipping here loses nothing.
     */
    if (!shouldRunRouterGuards.value) return

    void replaceCurrentLocationForGuardRerun().catch(() => {
      // duplicate/aborted navigation
    })
  }

  const setIsAppLoaded = (value: boolean) => {
    if (!value) {
      isAppLoaded.value = false
      return
    }

    isBootstrapRouteRevalidation.value = true
    void replaceCurrentLocationForGuardRerun()
      .catch(() => {
        // duplicate/aborted navigation — still show the shell
      })
      .finally(() => {
        isBootstrapRouteRevalidation.value = false
        isAppLoaded.value = true
      })
  }

  const isMac = computed(() => /Mac|iPod|iPhone|iPad/.test(navigator.userAgent))

  const showModalSelector = ref(true)
  const setShowModalSelector = (value: boolean) =>
    (showModalSelector.value = value)

  const setAllowUploads = (value: boolean) => {
    allowUploads.value = value
  }

  return {
    isUserAuthorisedForEverGpt,
    isNotAuthorisedPage,
    setIsUserAuthorisedForEverGpt,
    isAppLoaded,
    setIsAppLoaded,
    shouldRunRouterGuards,
    isMac,
    showModalSelector,
    setShowModalSelector,
    allowUploads,
    setAllowUploads,
  }
})
